import { Component, CUSTOM_ELEMENTS_SCHEMA, effect, inject, input, Input, OnInit, Signal } from '@angular/core';
import { TranslationService, TranslationsLanguage } from '@lib/rs-ngx';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UserProfileForm } from '@shared/components/user-profile-form/models/user-profile-form';
import { AccountWithManager } from '@review-company-manager/models/account-with-manager';
import { impersonalEmailValidator } from '../../validators/impersonalEmailValidator';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { MatInput } from '@angular/material/input';
import {
  RsAddClearFormFieldButtonDirective
} from '@rs-shared-lib/directives/rs-add-clear-form-field-button/rs-add-clear-form-field-button.directive';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { toSignal } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs';
import { MatButton } from '@angular/material/button';
import AuthorizedRepresentativeDialogComponent
  from '@shared/components/user-profile-form/components/authorized-representative-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'mgt-user-profile-form',
  templateUrl: './user-profile-form.component.html',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatError,
    TranslateModule,
    MatInput,
    RsAddClearFormFieldButtonDirective,
    MatSelect,
    MatOption,
    MatLabel,
    MatRadioGroup,
    MatRadioButton,
    MatButton,

  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  styleUrls: ['./user-profile-form.component.scss'],
})
export class UserProfileFormComponent implements OnInit {
  public readonly userProfileForm: FormGroup<UserProfileForm>;
  public readonly languages: TranslationsLanguage[];
  public readonly isLegalRepresentative: Signal<boolean>;

  public readonly filterImpersonalEmails = input(false);
  public readonly isEmailDisabled = input(true);
  public readonly initialValue = input<AccountWithManager | null>(null);
  public readonly showAuthorizedRepresentative = input(false);

  private readonly dialog = inject(MatDialog);

  constructor(private readonly translationService: TranslationService) {
    this.languages = this.translationService.languages;
    this.userProfileForm = new FormGroup<UserProfileForm>({
      email: new FormControl(
        {
          value: '',
          disabled: this.isEmailDisabled(),
        },
        {
          nonNullable: true,
          validators: [Validators.required, Validators.email],
        },
      ),
      firstName: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(29)],
      }),
      lastName: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(30)],
      }),
      language: new FormControl(null, [Validators.required]),
      jobTitle: new FormControl('', { nonNullable: true }),
      phone: new FormControl('', {
        nonNullable: true,
        validators: [Validators.maxLength(20), Validators.pattern(/^[+{0}\d(][\s0-9()/]+[0-9]$/)],
      }),
      mobile: new FormControl('', {
        nonNullable: true,
        validators: [Validators.maxLength(20), Validators.pattern(/^[+{0}\d(][\s0-9()/]+[0-9]$/)],
      }),
      isLegalRepresentative: new FormControl(
        { value: true, disabled: true },
        { nonNullable: true },
      ),
      legalRepresentativeEmail: new FormControl(
        { value: '', disabled: true },
      ),
      sendRSIEmail: new FormControl(
        { value: '', disabled: true },
        { nonNullable: true },
      ),
    });

    this.isLegalRepresentative = toSignal(
      this.userProfileForm.controls.isLegalRepresentative.valueChanges.pipe(
        startWith(this.userProfileForm.controls.isLegalRepresentative.value),
      ),
      { initialValue: this.userProfileForm.controls.isLegalRepresentative.value },
    );

    effect(() => {
      this.filterImpersonalEmails() ? this.addImpersonalEmailValidator() : this.removeImpersonalEmailValidator();
      !this.isLegalRepresentative() ? this.addLegalRepresentativeEmailValidator() : this.removeLegalRepresentativeEmailValidator();
      this.userProfileForm.controls.legalRepresentativeEmail[!this.isLegalRepresentative() ? 'enable' : 'disable']();
    });
  }

  @Input() public set disabled(isDisabled: boolean) {
    if (isDisabled) {
      this.userProfileForm.disable();
    } else {
      this.userProfileForm.enable();
    }
  }

  public ngOnInit(): void {
    if (this.isEmailDisabled()) {
      this.userProfileForm.controls.email.disable();
    } else {
      this.userProfileForm.controls.email.enable();
    }

    if (this.initialValue()) {
      this.userProfileForm.patchValue(this.initialValue()!);
    }

    if (this.showAuthorizedRepresentative()) {
      this.addAuthorizedRepresentativeEmailValidator();
    }
  }

  public openAuthorizedRepresentativeDialog(): void {
    this.dialog.open(AuthorizedRepresentativeDialogComponent, {
      width: '800px',
      autoFocus: false,
      panelClass: 'data-test-id-authorized-representative-dialog',
    });
  }

  private addImpersonalEmailValidator(): void {
    this.userProfileForm.controls.email.addValidators(impersonalEmailValidator());
    this.userProfileForm.controls.email.updateValueAndValidity();
  }

  private removeImpersonalEmailValidator(): void {
    this.userProfileForm.controls.email.removeValidators(impersonalEmailValidator());
    this.userProfileForm.controls.email.updateValueAndValidity();
  }

  private addLegalRepresentativeEmailValidator(): void {
    this.userProfileForm.controls.legalRepresentativeEmail.addValidators([Validators.email, Validators.required]);
    this.userProfileForm.controls.legalRepresentativeEmail.updateValueAndValidity();
  }

  private removeLegalRepresentativeEmailValidator(): void {
    this.userProfileForm.controls.legalRepresentativeEmail.removeValidators([Validators.email, Validators.required]);
    this.userProfileForm.controls.legalRepresentativeEmail.updateValueAndValidity();
  }

  private addAuthorizedRepresentativeEmailValidator(): void {
    this.userProfileForm.controls.isLegalRepresentative.enable();
    this.userProfileForm.controls.isLegalRepresentative.addValidators(Validators.required);
    this.userProfileForm.controls.isLegalRepresentative.updateValueAndValidity();

    this.userProfileForm.controls.sendRSIEmail.enable();
    this.userProfileForm.controls.sendRSIEmail.addValidators([Validators.email, Validators.required]);
    this.userProfileForm.controls.sendRSIEmail.updateValueAndValidity();
  }
}
